import { useEffect, useMemo, useRef, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { isEmpty } from 'lodash-es';

import { logger } from 'utils/logger';
import { ERROR_REASONS } from 'utils/logger/transports/constants';

import LoginRoutes from './LoginRoutes';
import MainRoutes from './MainRoutes';
import { useAppContext } from 'store/AppProvider';

type RouteType = 'login' | 'no-permission' | 'main';

const getRouteType = (token: string | undefined, permissionRolesLength: number): RouteType => {
    if (!token) return 'login';
    return permissionRolesLength ? 'main' : 'no-permission';
};

const Router = () => {
    const { appState } = useAppContext();
    const token = appState.user.user?.token;
    const permissionRoles = appState.generalData.permissionAvailableRoles;

    const [routerKey, setRouterKey] = useState(0);
    const currentRouteTypeRef = useRef<RouteType | null>(null);

    const routeType = getRouteType(token, permissionRoles.length);

    useEffect(() => {
        try {
            if (routeType !== currentRouteTypeRef.current) {
                // A flip away from 'main' mid-session causes the entire RouterProvider
                // to remount and wipes in-route state (form values, current tab, etc.).
                // 'login' -> anything and initial null -> anything are normal startup
                // transitions; 'main' -> not-main is the symptom users report as
                // "the page restarted while I was working".
                if (currentRouteTypeRef.current === 'main' && routeType !== 'main') {
                    logger.warn('Unexpected router remount: main -> ' + routeType, {
                        tags: {
                            reason: ERROR_REASONS.unexpectedRouterRemount,
                            from: 'main',
                            to: routeType,
                            hasToken: String(!!token),
                            rolesLen: String(permissionRoles.length),
                            url: window.location.pathname,
                        },
                    });
                }
                currentRouteTypeRef.current = routeType;
                setRouterKey((prev) => prev + 1);
            }
        } catch (error) {
            logger.error(Error('Error resolving router:'), { cause: error });
        }
    }, [routeType, token, permissionRoles.length]);

    const routes = useMemo(() => {
        if (!token) return LoginRoutes;
        return permissionRoles.length ? MainRoutes.Routes : MainRoutes.NoPermissionRoutes;
    }, [routeType]);

    // Create the router only when routes change
    const router = useMemo(() => {
        if (isEmpty(routes)) return null;

        return createBrowserRouter(routes);
    }, [routes]);

    if (!router) return null;

    return <RouterProvider router={router} key={routerKey} />;
};

export default Router;
